import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { Chatbot } from './components/Chatbot';
import { ErrorBoundary } from './components/ErrorBoundary'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ToastProvider } from './contexts/ToastContext'
import { CookieConsent } from './components/CookieConsent'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import { trackPageView } from './lib/analytics'

const Home = lazy(() => import('./pages/Home'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Install = lazy(() => import('./pages/Install'))
const Settings = lazy(() => import('./pages/Settings'))
const Login = lazy(() => import('./pages/Login'))
const Signup = lazy(() => import('./pages/Signup'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const AgencyContact = lazy(() => import('./pages/AgencyContact'))
const Docs = lazy(() => import('./pages/Docs'))
const Blog = lazy(() => import('./pages/Blog'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const EnableMFA = lazy(() => import('./pages/EnableMFA'))
const Features = lazy(() => import('./pages/Features'))
const About = lazy(() => import('./pages/About'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const Referrals = lazy(() => import('./pages/Referrals'))
const Security = lazy(() => import('./pages/Security'))
const APIDocs = lazy(() => import('./pages/APIDocs'))
const BayesianBandits = lazy(() => import('./pages/blog/BayesianBandits'))
const LowTrafficABTesting = lazy(() => import('./pages/blog/LowTrafficABTesting'))
const CopywritingMistakes = lazy(() => import('./pages/blog/CopywritingMistakes'))
const AICopywriting = lazy(() => import('./pages/blog/AICopywriting'))
const LandingPageCRO = lazy(() => import('./pages/blog/LandingPageCRO'))
const CaseStudySaaSMetrics = lazy(() => import('./pages/blog/CaseStudySaaSMetrics'))

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);
  return null;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-2 border-dash-accent border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

export default function App() {
  return (
    <ThemeProvider>
      <CookieConsent />
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AnalyticsTracker />
              <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[999] focus:px-4 focus:py-2 focus:bg-[#e5ac41] focus:text-[#0a0a0a] focus:rounded-lg focus:font-semibold">
                Skip to main content
              </a>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Layout><Home /></Layout>} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/install" element={<ProtectedRoute><Install /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/agency" element={<AgencyContact />} />
                  <Route path="/docs" element={<Layout><Docs /></Layout>} />
                  <Route path="/blog" element={<Layout><Blog /></Layout>} />
                  <Route path="/blog/bayesian-bandits-landing-pages" element={<Layout><BayesianBandits /></Layout>} />
                  <Route path="/blog/ab-test-low-traffic" element={<Layout><LowTrafficABTesting /></Layout>} />
                  <Route path="/blog/copywriting-mistakes-conversions" element={<Layout><CopywritingMistakes /></Layout>} />
                  <Route path="/blog/ai-copywriting-vs-human" element={<Layout><AICopywriting /></Layout>} />
                  <Route path="/blog/landing-page-cro-saas" element={<Layout><LandingPageCRO /></Layout>} />
                  <Route path="/blog/case-study-saasmetrics-ctr" element={<Layout><CaseStudySaaSMetrics /></Layout>} />
                  <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
                  <Route path="/terms" element={<Layout><Terms /></Layout>} />
                  <Route path="/mfa" element={<ProtectedRoute><EnableMFA /></ProtectedRoute>} />
                  <Route path="/features" element={<Layout><Features /></Layout>} />
                  <Route path="/about" element={<Layout><About /></Layout>} />
                  <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
                  <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
                  <Route path="/security" element={<Layout><Security /></Layout>} />
                  <Route path="/api-docs" element={<Layout><APIDocs /></Layout>} />
                </Routes>
              </Suspense>
              <Chatbot />
            </ErrorBoundary>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
